import { DateTime } from 'luxon';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import {
  CarrierEDIPartnerDTO,
  CarrierEDIRelationshipStatus,
  ShipmentPointDTO,
} from './VehiclesDTO';
import { joinStrings } from 'shared/utils/StringUtils';

interface RelationshipStatusCopy {
  label: string;
}

const RELATIONSHIP_STATUSES: Record<
  CarrierEDIRelationshipStatus,
  RelationshipStatusCopy
> = {
  moving_spot_loads: { label: 'Moving Spot Loads' },
  under_contract: { label: 'Under Contract' },
  in_discussions: { label: 'In Discussions' },
};

function findRelationshipStatus(
  status: string,
): undefined | RelationshipStatusCopy {
  return status in RELATIONSHIP_STATUSES
    ? RELATIONSHIP_STATUSES[status as CarrierEDIRelationshipStatus]
    : undefined;
}

export function getRelationshipStatusLabel(status: string): string {
  return findRelationshipStatus(status)?.label ?? status;
}

export function getPartnerSummary({
  relationship_status,
  expected_vins_per_month,
}: CarrierEDIPartnerDTO): string {
  return joinStrings(
    ' · ',
    getRelationshipStatusLabel(relationship_status),
    expected_vins_per_month && `${expected_vins_per_month} VINs/month`,
  );
}

export function formatAge(createdAt: string): string {
  const created = DateTime.fromISO(createdAt);
  const now = DateTime.now();
  const diff = now.diff(created, ['days', 'hours', 'minutes', 'seconds']);

  const days = Math.floor(diff.days);

  if (days >= 1) {
    return `${days} ${formatPlural(days, 'day', 'days')}`;
  }

  const hours = Math.floor(diff.hours);

  if (hours >= 1) {
    return `${hours} ${formatPlural(hours, 'hour', 'hours')}`;
  }

  const minutes = Math.floor(diff.minutes);

  if (minutes >= 1) {
    return `${minutes} ${formatPlural(minutes, 'minute', 'minutes')}`;
  }

  return 'just now';
}

export function formatDateRange(
  startDate?: string | null,
  endDate?: string | null,
): string {
  if (!startDate || !endDate) return '';

  const start = new Date(startDate);
  const end = new Date(endDate);

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endDay = end.getDate();

  if (start.toDateString() === end.toDateString()) {
    return `${startMonth} ${startDay}`;
  }

  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${startMonth} ${startDay}–${endDay}`;
  }

  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  return `${startMonth} ${startDay}–${endMonth} ${endDay}`;
}

export function formatPickupDate(date?: string | null): string {
  if (!date) return '';

  const pickupDate = new Date(date);
  const dayOfWeek = pickupDate.toLocaleDateString('en-US', {
    weekday: 'short',
  });
  const month = pickupDate.toLocaleDateString('en-US', { month: 'short' });
  const day = pickupDate.getDate();

  return `No later than ${dayOfWeek}, ${month} ${day}`;
}

function formatLocation(point?: ShipmentPointDTO): string {
  if (!point) return '';
  const cityState = joinStrings(', ', point.city, point.state);
  return cityState ? `${point.code} / ${cityState}` : point.code || '';
}

export function formatRoute(
  origin?: ShipmentPointDTO,
  destination?: ShipmentPointDTO,
): string {
  const originAddress = formatLocation(origin);
  const destinationAddress = formatLocation(destination);
  return joinStrings(' → ', originAddress, destinationAddress);
}
